{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2023                                      }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.NFC;

{$DEFINE NOPP}

interface

uses
  Classes, libnfc, web, js;

type
  TJSNDEFMessageRecord = record
    jsmessage: TJSNDEFMessage;
  end;

  TNFCReadingEvent = procedure(Sender: TObject; ASerialNumber: string; AData: TJSNDEFMessageRecord) of object;

  TNFC = class(TComponent)
  private
    FReader: TJSNDEFReader;
    FAbortController: TJSAbortController;
    FIgnoreRead: Boolean;
    FScanning: Boolean;
    FOnReadingError: TNotifyEvent;
    FOnReading: TNFCReadingEvent;
    function GetNDEFReader: TJSNDEFReader;
    function GetAbortController: TJSAbortController;
  protected
    function HandleReading(Event: TJSNDEFReadingEvent): Boolean;
    function HandleReadingError(Event: TJSEvent): Boolean;
    property NDEFReader: TJSNDEFReader read GetNDEFReader;
    property AbortController: TJSAbortController read GetAbortController;
    function WriteCustomInternal(ARecordType: string; AData: JSValue; AID: string; AMediaType: string): TJSPromise;
  public
    constructor Create(AOwner: TComponent); override;
    function Scan: TJSPromise;
    procedure Stop;
    function WriteEmpty: TJSPromise;
    function WriteString(AData: string): TJSPromise;
    function WriteURL(AURL: string): TJSPromise;
    function WriteCustom(ARecordType: string; AData: TJSUint8Array = nil; AID: string = ''; AMediaType: string = ''): TJSPromise; overload;
    function WriteCustom(ARecordType: string; AData: string; AID: string = ''; AMediaType: string = ''): TJSPromise; overload;
  published
    property OnReading: TNFCReadingEvent read FOnReading write FOnReading;
    property OnReadingError: TNotifyEvent read FOnReadingError write FOnReadingError;
  end;

  TWebNFC = class(TNFC);

  function IsNFCSupported: Boolean;

implementation

function IsNFCSupported: Boolean; assembler;
asm
  return typeof(window.NDEFReader) != "undefined";
end;

{ TNFC }

constructor TNFC.Create(AOwner: TComponent);
begin
  inherited;
  FIgnoreRead := False;
  FScanning := False;
end;

function TNFC.GetAbortController: TJSAbortController;
begin
  if not Assigned(FAbortController) then
    FAbortController := TJSAbortController.new;

  Result := FAbortController;
end;

function TNFC.GetNDEFReader: TJSNDEFReader;
begin
  if not Assigned(FReader) then
  begin
    FReader := TJSNDEFReader.new;
    FReader.addEventListener('reading', @HandleReading);
    FReader.addEventListener('readingerror', @HandleReadingError);
  end;

  Result := FReader;
end;

function TNFC.HandleReading(Event: TJSNDEFReadingEvent): Boolean;
var
  rec: TJSNDEFMessageRecord;
begin
  Result := True;
  if not FIgnoreRead and Assigned(OnReading) then
  begin
    rec.jsmessage := event.&message;
    OnReading(Self, Event.serialNumber, rec);
  end;
end;

function TNFC.HandleReadingError(Event: TJSEvent): Boolean;
begin
  Result := True;
  if Assigned(OnReadingError) then
    OnReadingError(Self);
end;

function TNFC.Scan: TJSPromise;
var
  o: TJSObject;
begin
  o := new(['signal', AbortController.signal]);

  FScanning := True;
  Result := NDEFReader.scan(o);
end;

procedure TNFC.Stop;
begin
  if FScanning then
    AbortController.abort;
end;

function TNFC.WriteCustom(ARecordType: string; AData: TJSUint8Array; AID,
  AMediaType: string): TJSPromise;
begin
  Result := WriteCustomInternal(ARecordType, AData, AID, AMediaType);
end;

function TNFC.WriteCustom(ARecordType, AData, AID,
  AMediaType: string): TJSPromise;
begin
  Result := WriteCustomInternal(ARecordType, AData, AID, AMediaType);
end;

function TNFC.WriteCustomInternal(ARecordType: string; AData: JSValue;
  AID, AMediaType: string): TJSPromise;
var
  msg: TJSNDEFMessageInit;
  d: TJSObject;
begin
  d := TJSObject.new;
  d.Properties['recordType'] := ARecordType;

  if JS.hasValue(AData) then
    d.Properties['data'] := AData;

  if AID <> '' then
    d.Properties['id'] := AID;

  if AMediaType <> '' then
    d.Properties['mediaType'] := AMediaType;

  msg := TJSNDEFMessageInit.new;
  msg.records := TJSArray.new;
  msg.records.push(d);

  if FScanning then
  begin
    FIgnoreRead := True;
    Result := TJSPromise.New(
    procedure (AResolve, AReject: TJSPromiseResolver)
    var
      arr: TJSUint8Array;
      s: string;
      o: TJSObject;
    begin
      o := new(['once', true]);
      NDEFReader.addEventListener(
        'reading',
        function (Event: TJSEvent): Boolean
        begin
          Result := True;
          NDEFReader.write(msg)._then(AResolve, AReject)._finally(procedure
          begin
            FIgnoreRead := False;
          end);
        end,
        o
      );
    end);
  end
  else
    Result := NDEFReader.write(msg);
end;

function TNFC.WriteEmpty: TJSPromise;
begin
  Result := WriteCustom('empty');
end;

function TNFC.WriteString(AData: string): TJSPromise;
begin
  Result := WriteCustom('text', AData);
end;

function TNFC.WriteURL(AURL: string): TJSPromise;
begin
  Result := WriteCustom('url', AURL);
end;

end.
